import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, Minus, ShoppingBag, Coffee } from "lucide-react";
import { CAFE } from "@/constants/testIds";
import { formatMoney } from "@/lib/currency";
import { toast } from "sonner";

const CATEGORIES = ["all", "coffee", "cold-brew", "tea", "food", "pastry"];

export default function POS() {
  const [menu, setMenu] = useState([]);
  const [category, setCategory] = useState("all");
  const [cart, setCart] = useState([]); // {id, name, price, qty}
  const [tableNumber, setTableNumber] = useState("");
  const [customer, setCustomer] = useState("");

  const load = async () => {
    const { data } = await api.get("/cafe/menu");
    setMenu(data);
  };

  useEffect(() => { load(); }, []);

  const filteredMenu = useMemo(
    () => menu.filter((m) => category === "all" || m.category === category),
    [menu, category]
  );

  const add = (item) => {
    setCart((c) => {
      const existing = c.find((x) => x.id === item.id);
      if (existing) return c.map((x) => (x.id === item.id ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { id: item.id, name: item.name, price: item.price, qty: 1 }];
    });
  };

  const dec = (id) =>
    setCart((c) =>
      c.map((x) => (x.id === id ? { ...x, qty: x.qty - 1 } : x)).filter((x) => x.qty > 0)
    );

  const remove = (id) => setCart((c) => c.filter((x) => x.id !== id));

  const total = cart.reduce((s, x) => s + x.price * x.qty, 0);

  const checkout = async () => {
    if (cart.length === 0) return;
    try {
      await api.post("/cafe/orders", {
        table_number: tableNumber || null,
        customer_name: customer || null,
        order_type: tableNumber ? "dine-in" : "takeaway",
        lines: cart.map((x) => ({ menu_item_id: x.id, quantity: x.qty })),
      });
      toast.success(`Order placed · ${formatMoney(total)}`);
      setCart([]);
      setTableNumber("");
      setCustomer("");
    } catch {
      toast.error("Could not place order.");
    }
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Café</div>
        <h1 className="font-display text-4xl">Point of Sale</h1>
        <p className="text-muted-foreground text-sm mt-1">Tap items to build an order.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Menu grid */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-2 mb-4">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={
                  "text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border transition-colors " +
                  (category === c
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:text-foreground")
                }
              >
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filteredMenu.map((m) => (
              <button
                key={m.id}
                data-testid={CAFE.cartAdd(m.id)}
                onClick={() => add(m)}
                className="group text-left rounded-xl border border-border bg-card overflow-hidden hover:-translate-y-0.5 transition-transform"
              >
                <div className="aspect-square bg-muted overflow-hidden">
                  {m.image_url ? (
                    <img src={m.image_url} className="h-full w-full object-cover" alt="" />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-muted-foreground">
                      <Coffee className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-medium text-sm truncate">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">{m.category}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="font-display text-xl">{formatMoney(m.price)}</div>
                    <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Cart */}
        <div className="rounded-xl border border-border bg-card p-5 h-fit sticky top-20">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-2xl inline-flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" /> Cart
            </div>
            <span className="text-xs text-muted-foreground">{cart.reduce((s, x) => s + x.qty, 0)} items</span>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <Input placeholder="Table #" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} />
            <Input placeholder="Name (opt)" value={customer} onChange={(e) => setCustomer(e.target.value)} />
          </div>

          <div className="space-y-3 min-h-[120px]">
            {cart.length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">
                Add items to get started.
              </div>
            )}
            {cart.map((x) => (
              <div key={x.id} className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{x.name}</div>
                  <div className="text-xs text-muted-foreground">{formatMoney(x.price)} each</div>
                </div>
                <div className="flex items-center gap-1 border border-border rounded-full">
                  <button onClick={() => dec(x.id)} className="h-7 w-7 grid place-items-center text-muted-foreground hover:text-foreground">
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-5 text-center text-sm">{x.qty}</span>
                  <button onClick={() => add({ id: x.id, name: x.name, price: x.price })} className="h-7 w-7 grid place-items-center text-muted-foreground hover:text-foreground">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button onClick={() => remove(x.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-border">
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Total</div>
              <div className="font-display text-3xl">{formatMoney(total)}</div>
            </div>
            <Button
              data-testid={CAFE.cartCheckout}
              onClick={checkout}
              disabled={cart.length === 0}
              className="w-full rounded-full h-11"
            >
              Send to kitchen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
